import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { msalInstance, loginRequest } from '../authConfig.js'

const AuthContext = createContext()

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'

// Turns a raw MSAL/Entra error into something a visitor to the login screen
// can actually act on — the technical code (e.g. "interaction_in_progress",
// with a link to Microsoft's error docs) is logged for debugging but never
// shown on screen. Errors we threw ourselves (e.g. "Kein Zugang...") already
// have a friendly message and are passed through as-is.
function friendlyAuthError(err) {
  console.error('MSAL auth error:', err)
  if (!err?.errorCode) return err?.message || 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.'
  if (err.errorCode === 'interaction_in_progress') {
    return 'Die Anmeldung hängt fest. Bitte versuchen Sie es erneut.'
  }
  return 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.'
}

// "interaction_in_progress" means MSAL thinks a login is still running — often
// a stale flag left behind by an interrupted redirect (tab closed mid-flow,
// dev-mode double-invoke, etc.). Clearing it lets a retry actually go through
// instead of failing with the same error forever.
function clearStuckInteraction() {
  Object.keys(sessionStorage)
    .filter(k => k.endsWith('.interaction.status'))
    .forEach(k => sessionStorage.removeItem(k))
}

export const AuthProvider = ({ children }) => {
  const [account,   setAccount]   = useState(null)   // MSAL account object
  const [admin,     setAdmin]     = useState(null)   // { email, role, tenant_id, features, … }
  const [loading,   setLoading]   = useState(true)
  const [authError, setAuthError] = useState(null)

  // ---------------------------------------------------------------------------
  // Token helper — always returns a fresh access token.
  // Silent first (uses cache / refresh token), popup fallback if interaction
  // is required (MFA prompt, consent, etc.)
  // ---------------------------------------------------------------------------
  const getToken = useCallback(async (forceRefresh = false) => {
    const current = msalInstance.getActiveAccount()
    if (!current) throw new Error('No active account')
    try {
      const result = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: current,
        forceRefresh,
      })
      return result.accessToken
    } catch {
      const result = await msalInstance.acquireTokenPopup({
        ...loginRequest,
        account: current,
      })
      return result.accessToken
    }
  }, [])

  // ---------------------------------------------------------------------------
  // Fetch the viGuard admin profile (role / tenant / features) from the backend.
  // Entra ID proves identity; the backend decides what this person can do.
  // ---------------------------------------------------------------------------
  const fetchAdminProfile = async () => {
    const token = await getToken()
    const res = await fetch(`${BACKEND_URL}/api/admin/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) {
      if (res.status === 403) msalInstance.clearCache()
      throw new Error('Kein Zugang für dieses Microsoft-Konto.')
    }
    return res.json()
  }

  // ---------------------------------------------------------------------------
  // Internal: sign in via Microsoft and load the admin profile
  // ---------------------------------------------------------------------------
  const doLogin = async () => {
    // loginRedirect navigates the page away to Microsoft — nothing after this runs.
    // The account is set by handleRedirectPromise() in index.js on the way back.
    await msalInstance.loginRedirect(loginRequest)
  }

  // ---------------------------------------------------------------------------
  // On mount: restore existing session or trigger Microsoft login immediately.
  // By the time this runs, msalInstance.initialize() has already completed
  // (enforced in index.js), so getAllAccounts() is accurate.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const accounts = msalInstance.getAllAccounts()

    if (accounts.length > 0) {
      // Existing session — restore silently, no popup
      msalInstance.setActiveAccount(accounts[0])
      setAccount(accounts[0])
      fetchAdminProfile()
        .then(data => setAdmin(data))
        .catch(err => setAuthError(friendlyAuthError(err)))
        .finally(() => setLoading(false))
    } else {
      // No session — trigger Microsoft login popup immediately (Option B)
      doLogin()
        .catch(err => {
          if (err?.errorCode === 'interaction_in_progress') clearStuckInteraction()
          setAuthError(friendlyAuthError(err))
        })
        .finally(() => setLoading(false))
    }
  }, []) // intentional — runs once on mount only

  // ---------------------------------------------------------------------------
  // Retry — called when the popup was blocked or the user cancelled
  // ---------------------------------------------------------------------------
  const retryLogin = () => {
    setAuthError(null)
    setLoading(true)
    clearStuckInteraction()
    // Force Microsoft to show the account picker so the user can switch accounts
    msalInstance.loginRedirect({ ...loginRequest, prompt: 'select_account' })
      .catch(err => {
        setAuthError(friendlyAuthError(err))
        setLoading(false)
      })
  }

  // ---------------------------------------------------------------------------
  // Logout — clears MSAL cache, then re-triggers login
  // ---------------------------------------------------------------------------
  const logout = async () => {
    // logoutRedirect navigates away to Microsoft to clear the session,
    // then redirects back. On return, no account is found and doLogin()
    // triggers the login redirect again automatically.
    await msalInstance.logoutRedirect()
  }

  // ---------------------------------------------------------------------------
  // apiFetch — drop-in replacement for fetch() used by every page.
  // Automatically attaches a fresh Azure Bearer token to every request.
  // ---------------------------------------------------------------------------
  const apiFetch = async (path, options = {}) => {
    const makeRequest = (token) =>
      fetch(`${BACKEND_URL}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(options.headers || {}),
        },
      })

    const token = await getToken()
    const res = await makeRequest(token)

    if (res.status !== 401) return res

    // Token may be stale — force-refresh and retry once
    const freshToken = await getToken(true)
    const retryRes = await makeRequest(freshToken)

    if (retryRes.status === 401) {
      // Still unauthorized after fresh token — clear session and re-login
      await msalInstance.logoutRedirect()
    }

    return retryRes
  }

  return (
    <AuthContext.Provider value={{
      account,
      admin,
      loading,
      authError,
      retryLogin,
      logout,
      apiFetch,
      getToken,
      isOwner: admin?.role === 'owner',
      isUser:  admin?.role === 'user',
      features: admin?.features || {
        allow_custom_rules: false,
        allow_quiz: true,
        allow_language_management: true,
        info_pages_enabled: true,
        welcome_screen_enabled: true,
        sip_enabled: false,
        standby_settings_enabled: false,
      },
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
